/**
 * Morph Tracing — interactions, tools, and manual spans.
 *
 * An `Interaction` is one user turn / agent run. It threads association
 * properties (user_id / convo_id / event_id) onto every span created inside it —
 * including the auto-instrumented LLM spans — so a whole conversation stitches
 * together in the Morph UI. Built on Traceloop's `withTask` / `withTool` and a
 * manual tracer for already-completed tool spans.
 */
import { context, SpanStatusCode, trace, type Span } from '@opentelemetry/api';
import * as traceloop from '@traceloop/node-server-sdk';

import { metadata as buildMetadata } from './otel.js';
import { REFLEX_RUN_ATTRIBUTE, serializeEvals } from './reflex.js';
import type {
  FinishOptions,
  SpanParams,
  ToolParams,
  ToolSpan,
  TrackToolParams,
  TraceContext,
} from './types.js';

// Traceloop semantic-convention attribute keys.
const ASSOC = 'traceloop.association.properties.';
const ENTITY_INPUT = 'traceloop.entity.input';
const ENTITY_OUTPUT = 'traceloop.entity.output';
const ENTITY_NAME = 'traceloop.entity.name';
const SPAN_KIND = 'traceloop.span.kind';

function uuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxxxxxx4xxxyxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function asString(v: unknown): string {
  if (v == null) return '';
  return typeof v === 'string' ? v : JSON.stringify(v);
}

export interface Interaction {
  getEventId(): string | undefined;
  setInput(input: string): void;
  setProperty(key: string, value: string): void;
  setProperties(props: Record<string, string>): void;
  /**
   * Run `fn` with this interaction's association properties active — required so
   * auto-instrumented OpenAI/Anthropic spans inherit user_id / convo_id / tags.
   */
  run<T>(fn: () => Promise<T> | T): Promise<T>;
  /** Run `fn` inside a traced task span; LLM calls within inherit attribution. */
  withSpan<T>(params: SpanParams | string, fn: () => Promise<T> | T): Promise<T>;
  /** Run `fn` inside a traced tool span. */
  withTool<T>(params: ToolParams | string, fn: () => Promise<T> | T): Promise<T>;
  /** Start a tool span you end manually. */
  startToolSpan(params: ToolParams | string): ToolSpan;
  /** Record an already-completed tool invocation. */
  trackTool(params: TrackToolParams): void;
  /** Metadata for the Vercel AI SDK `experimental_telemetry.metadata`. */
  vercelAiSdkMetadata(): Record<string, string>;
  /** End the interaction with its final output. */
  finish(opts: FinishOptions | string): Promise<void>;
}

export type Tracer = Pick<Interaction, 'withSpan' | 'withTool' | 'startToolSpan' | 'trackTool'>;

/** Build the association-property bag Traceloop propagates onto child spans. */
function associationProps(
  ctx: TraceContext & { userId?: string },
  extra: Record<string, string>,
): Record<string, string> {
  const props: Record<string, string> = { ...extra };
  if (ctx.userId) props.user_id = ctx.userId;
  if (ctx.convoId) props.convo_id = ctx.convoId;
  if (ctx.eventId) props.event_id = ctx.eventId;
  if (ctx.event) props.event_name = ctx.event;
  return props;
}

export function createInteractionApi(
  initial: TraceContext & { userId?: string },
  traceContent: boolean,
  onClose?: (eventId: string) => void,
): Interaction {
  const ctx: TraceContext & { userId?: string } = {
    ...initial,
    eventId: initial.eventId ?? uuid(),
  };
  const properties: Record<string, string> = { ...(initial.properties ?? {}) };
  let input = initial.input;
  let workflowSpan: Span | null = null;
  let finished = false;
  // Serialized once. Set as a raw attribute on the workflow span only — NOT an
  // association property, so it is not copied onto every child LLM span. Ingest
  // reads `morph.reflex.run` off the workflow-span to enqueue the evals.
  const reflexRun = serializeEvals(initial.evals);

  const withAssoc = <T>(fn: () => Promise<T> | T): Promise<T> | T =>
    traceloop.withAssociationProperties(associationProps(ctx, properties), fn);

  const workflowName = () => ctx.event ?? 'interaction';

  /** Open the interaction workflow span once; stays active until finish(). */
  function ensureWorkflowSpan(): Span {
    if (workflowSpan) return workflowSpan;
    const span = traceloop.getTraceloopTracer().startSpan(workflowName());
    span.setAttribute(SPAN_KIND, 'workflow');
    for (const [k, v] of Object.entries(associationProps(ctx, properties))) {
      span.setAttribute(ASSOC + k, v);
    }
    if (traceContent && input) span.setAttribute(ENTITY_INPUT, input);
    if (reflexRun) span.setAttribute(REFLEX_RUN_ATTRIBUTE, reflexRun);
    workflowSpan = span;
    return span;
  }

  /** Run fn with association props and the workflow span as the active parent. */
  function withWorkflowContext<T>(fn: () => Promise<T> | T): Promise<T> | T {
    return withAssoc(() => {
      const span = ensureWorkflowSpan();
      return context.with(trace.setSpan(context.active(), span), fn);
    });
  }

  /** Close the workflow span exactly once, applying final attributes. */
  function closeWorkflowSpan(apply: (span: Span) => void): void {
    if (finished) return;
    withAssoc(() => {
      const span = ensureWorkflowSpan();
      apply(span);
      span.end();
    });
    workflowSpan = null;
    finished = true;
    onClose?.(ctx.eventId!);
  }

  /** Record an exception + ERROR status on the workflow span, then close it. */
  function failWorkflowSpan(err: unknown): void {
    const e = err instanceof Error ? err : new Error(String(err));
    closeWorkflowSpan((span) => {
      span.recordException(e);
      span.setStatus({ code: SpanStatusCode.ERROR, message: e.message });
      if (traceContent) span.setAttribute(ENTITY_OUTPUT, `ERROR: ${e.message}`);
    });
  }

  const toolName = (p: ToolParams | string) => (typeof p === 'string' ? p : p.name);

  function startToolSpan(params: ToolParams | string): ToolSpan {
    const name = toolName(params);
    const span: Span = traceloop.getTraceloopTracer().startSpan(name);
    span.setAttribute(SPAN_KIND, 'tool');
    span.setAttribute(ENTITY_NAME, name);
    for (const [k, v] of Object.entries(associationProps(ctx, properties))) {
      span.setAttribute(ASSOC + k, v);
    }
    if (typeof params !== 'string' && params.properties) {
      for (const [k, v] of Object.entries(params.properties)) span.setAttribute(k, v);
    }
    return {
      setInput(value: unknown) {
        if (traceContent) span.setAttribute(ENTITY_INPUT, asString(value));
      },
      setOutput(value: unknown) {
        if (traceContent) span.setAttribute(ENTITY_OUTPUT, asString(value));
      },
      setError(error: Error | string) {
        const e = typeof error === 'string' ? new Error(error) : error;
        span.recordException(e);
        span.setStatus({ code: SpanStatusCode.ERROR, message: e.message });
      },
      end() {
        span.end();
      },
    };
  }

  return {
    getEventId: () => ctx.eventId,
    setInput(value: string) {
      input = value;
    },
    setProperty(key: string, value: string) {
      properties[key] = value;
    },
    setProperties(props: Record<string, string>) {
      Object.assign(properties, props);
    },
    vercelAiSdkMetadata() {
      return buildMetadata({
        userId: ctx.userId ?? 'unknown',
        convoId: ctx.convoId,
        eventName: ctx.event,
        eventId: ctx.eventId,
        properties,
      });
    },
    async run(fn) {
      try {
        return await Promise.resolve(withWorkflowContext(fn));
      } catch (err) {
        // Natively record the failure on the workflow span and ship it, so a
        // throwing interaction still lands in the trace as an errored span.
        failWorkflowSpan(err);
        throw err;
      }
    },
    withSpan(params, fn) {
      const name = typeof params === 'string' ? params : params.name;
      return Promise.resolve(withWorkflowContext(() => traceloop.withTask({ name }, fn)));
    },
    withTool(params, fn) {
      const name = toolName(params);
      const version = typeof params === 'string' ? undefined : params.version;
      return Promise.resolve(withWorkflowContext(() => traceloop.withTool({ name, version }, fn)));
    },
    startToolSpan,
    trackTool(params: TrackToolParams) {
      const span = startToolSpan({ name: params.name, properties: params.properties });
      if (params.input !== undefined) span.setInput(params.input);
      if (params.output !== undefined) span.setOutput(params.output);
      if (params.error) span.setError(params.error);
      span.end();
    },
    async finish(opts) {
      const output = typeof opts === 'string' ? opts : opts.output;
      if (typeof opts !== 'string' && opts.properties) Object.assign(properties, opts.properties);
      // No-op if run() already closed the span on error; otherwise end it now.
      // (Creates one if finish() is called alone, i.e. begin() → finish().)
      closeWorkflowSpan((span) => {
        if (traceContent) span.setAttribute(ENTITY_OUTPUT, output);
      });
    },
  };
}

/**
 * Inert Interaction for disabled instances. Preserves control flow — run() /
 * withTool() still execute their callback and rethrow errors — but never touches
 * the tracer. Required because the OTel provider is a process-wide singleton: a
 * disabled instance that created real spans would ship them through whichever
 * enabled instance registered the provider.
 */
export function createNoopInteraction(initial: TraceContext & { userId?: string }): Interaction {
  const ctx: TraceContext & { userId?: string } = {
    ...initial,
    eventId: initial.eventId ?? uuid(),
  };
  const properties: Record<string, string> = { ...(initial.properties ?? {}) };
  const noopToolSpan: ToolSpan = { setInput() {}, setOutput() {}, setError() {}, end() {} };

  return {
    getEventId: () => ctx.eventId,
    setInput() {},
    setProperty(key: string, value: string) {
      properties[key] = value;
    },
    setProperties(props: Record<string, string>) {
      Object.assign(properties, props);
    },
    vercelAiSdkMetadata() {
      return buildMetadata({
        userId: ctx.userId ?? 'unknown',
        convoId: ctx.convoId,
        eventName: ctx.event,
        eventId: ctx.eventId,
        properties,
      });
    },
    async run(fn) {
      return await fn();
    },
    async withSpan(_params, fn) {
      return await fn();
    },
    async withTool(_params, fn) {
      return await fn();
    },
    startToolSpan: () => noopToolSpan,
    trackTool() {},
    async finish() {},
  };
}
